import { ChangeDetectionStrategy, Component, inject, OnInit } from '@angular/core';
import { FormArray, FormControl, FormGroup } from '@angular/forms';

import { MaskitoElementPredicate, MaskitoOptions } from '@maskito/core';
import { select, Store } from '@ngrx/store';

import { compact, isEqual, isNil, isUndefined, map as lodashMap } from 'lodash-es';
import { map, throttleTime } from 'rxjs';
import { distinctUntilChanged, filter } from 'rxjs/operators';

import { FORM_SECTION } from '../../../../../constants/form.constant';
import { amountMask } from '../../../../../constants/maskito.constants';
import { NetSheetMiscItemTypeEnum } from '../../../../../enums/net-sheet';
import { checkFormValidity } from '../../../../../helpers/form';
import * as fromNetSheetActions from '../../../../../store/actions/net-sheet.actions';
import * as fromNetSheetSelectors from '../../../../../store/selectors/net-sheet.selectors';
import { FormSection } from '../../../../../typings/form';
import { NetSheetFormMiscItem } from '../../../../../typings/net-sheet';
import { GenericComponent } from '../../../../components/generic/generic.component';

type NetSheetMiscItemForm = FormGroup<{
  amount: FormControl<string | null>;
  description: FormControl<string | null>;
  type: FormControl<NetSheetMiscItemTypeEnum>;
}>;

@Component({
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [
    {
      provide: FORM_SECTION,
      useExisting: MiscItemsComponent
    }
  ],
  selector: 'red-net-sheet-misc-items',
  standalone: false,
  templateUrl: './misc-items.component.html'
})
export class MiscItemsComponent extends GenericComponent implements OnInit, FormSection {
  private store = inject(Store);

  form = new FormGroup({
    miscItems: new FormArray<NetSheetMiscItemForm>([this.createMiscItem()])
  });

  constructor() {
    super();

    this.form.valueChanges
      .pipe(
        map((data) => data.miscItems),
        filter((miscItems) => !isUndefined(miscItems))
      )
      .subscribe((miscItems) => {
        this.store.dispatch(
          fromNetSheetActions.updateMiscItems({
            payload: {
              miscItems: compact(
                lodashMap(miscItems, (item) => {
                  let result: NetSheetFormMiscItem | null = null;
                  if (!isNil(item.amount) && !isNil(item.description) && !isNil(item.type)) {
                    result = {
                      amount: item.amount,
                      description: item.description,
                      type: item.type
                    };
                  }
                  return result;
                })
              )
            }
          })
        );
      });
  }

  ngOnInit() {
    this.addUniqueSubscription(
      'fieldsDataSubscription',
      this.store
        .pipe(
          select(fromNetSheetSelectors.selectMiscItemsFieldsData),
          throttleTime(200, undefined, { leading: true, trailing: true }),
          distinctUntilChanged((previous, current) => isEqual(previous, current))
        )
        .subscribe((data) => {
          this.form.patchValue(
            {
              miscItems: lodashMap(data.miscItems, (item) => ({
                amount: item.amount,
                description: item.description,
                type: item.type
              }))
            },
            { emitEvent: false }
          );
        })
    );
  }

  get miscItems() {
    return this.form.controls.miscItems;
  }

  addMiscItem() {
    this.miscItems.push(this.createMiscItem());
    this.form.updateValueAndValidity();
  }

  removeMiscItem(index: number) {
    this.miscItems.removeAt(index);
    this.form.updateValueAndValidity();
  }

  createMiscItem() {
    return new FormGroup({
      amount: new FormControl<string | null>(null),
      description: new FormControl<string | null>(null),
      type: new FormControl<NetSheetMiscItemTypeEnum>(NetSheetMiscItemTypeEnum.Debit, {
        nonNullable: true
      })
    });
  }

  readonly netSheetMiscItemTypeEnum = NetSheetMiscItemTypeEnum;
  readonly amountMask: MaskitoOptions = amountMask;
  readonly maskPredicate: MaskitoElementPredicate = async (el) =>
    (el as unknown as HTMLIonInputElement).getInputElement();
  readonly valid = checkFormValidity(this.form);
}
